/**
 * @file Navbar.tsx
 * @description Component for rendering the navigation bar.
 * Handles user authentication state and provides login/logout functionality.
 */

import React, { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../../config/firebaseConfig";
import { useAuthState } from "react-firebase-hooks/auth";
import { toast } from "react-toastify";
import { useUserDetails } from "../../context/UserDetailsContext";
import GenerateContentButton from "../common/GenerateContentButton";
import GeneratePlanButton from "../common/GeneratePlanButton";
import Login from "../auth/Login";
import PersonIcon from "@mui/icons-material/Person";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import UserAvatar from "../common/UserAvatar";
import log from "../../config/logging";
import { useNavbarContext } from "../../context/NavbarContext";

const Navbar: React.FC = () => {
  const [user] = useAuthState(auth);
  const { userDetails } = useUserDetails();
  const { isLoginDropdownOpen, openLoginDropdown } = useNavbarContext();
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    if (isLoginDropdownOpen && dropdownRef.current) {
      dropdownRef.current.setAttribute("open", "");
    }
  }, [isLoginDropdownOpen, openLoginDropdown]);

  useEffect(() => {
    log.debug("Navbar userDetails updated:", userDetails);
  }, [userDetails]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      log.debug("User logged out successfully");
      navigate("/");
    } catch (error) {
      log.error("Error during logout:", error);
      toast.error("Errore durante il logout");
    }
  };

  const closeDropdown = () => {
    dropdownRef.current?.removeAttribute("open");
  };

  const handleMenuItemClick = (action?: () => void) => {
    return () => {
      closeDropdown();
      action?.();
    };
  };

  const handleLoginSuccess = () => {
    closeDropdown();
  };

  const handleSwitchToSignUp = () => {
    closeDropdown();
    navigate("/registrati");
  };

  return (
    <div className="min-h-0 p-0 navbar">
      <div className="flex-1"></div>
      <div className="flex-none">
        {user ? (
          <details
            ref={dropdownRef}
            className="block dropdown dropdown-end [&[open]>summary>svg]:rotate-180"
          >
            <summary className="flex gap-1 bg-transparent border-0 shadow-none outline-none hover:outline-none hover:border-0 btn hover:bg-transparent">
              <UserAvatar />
              <ExpandMoreRoundedIcon
                fontSize="large"
                className="text-white transition-transform opacity-50"
              />
            </summary>
            <ul
              tabIndex={0}
              className="menu menu-sm dropdown-content mt-3 z-[1] p-4 bg-base-100 rounded-2xl border border-gray-line [&>li]:text-base-content [&>li>a]:px-3 [&>li>a]:py-2 [&>li>a]:text-base [&>li>a]:font-normal [&>li.divider]:my-2 [&>li.divider]:h-px [&>li.divider]:mx-1 [&>li.divider]:p-0 [--rounded-btn:9999px]"
            >
              <li>
                <Link
                  to="/account"
                  className="flex flex-row gap-2 p-0 mb-4 flex-nowrap text-base/normal"
                  onClick={handleMenuItemClick()}
                >
                  <UserAvatar size="w-14" showIndicator={false} />
                  <div className="flex flex-col items-start justify-center">
                    <div className="font-medium text-xl/normal text-primary">
                      {userDetails?.firstName} {userDetails?.lastName}
                    </div>
                    <div className="text-sm/none">{user.email}</div>
                  </div>
                </Link>
              </li>
              <li onClick={handleMenuItemClick()}>
                <GenerateContentButton />
              </li>
              {userDetails?.isPremium && (
                <li onClick={handleMenuItemClick()}>
                  <GeneratePlanButton />
                </li>
              )}
              <li className="divider"></li>
              <li>
                <Link to="/archivio-contenuti" onClick={handleMenuItemClick()}>
                  Archivio contenuti
                </Link>
              </li>
              {userDetails?.isPremium && (
                <li>
                  <Link to="/archivio-piani" onClick={handleMenuItemClick()}>
                    Archivio piani editoriali
                  </Link>
                </li>
              )}
              <li className="divider"></li>
              <li>
                <Link to="/account" onClick={handleMenuItemClick()}>
                  Il mio account
                </Link>
              </li>
              {!userDetails?.isPremium && (
                <li>
                  <Link
                    to="/checkout"
                    onClick={handleMenuItemClick()}
                    className="text-primary !font-medium"
                  >
                    Passa al piano Premium
                  </Link>
                </li>
              )}
              <li>
                <Link to="/faq" onClick={handleMenuItemClick()}>
                  Faq
                </Link>
              </li>
              <li className="divider"></li>
              <li>
                <button
                  className="text-base font-normal"
                  onClick={handleMenuItemClick(handleLogout)}
                >
                  Logout
                </button>
              </li>
            </ul>
          </details>
        ) : (
          <details
            ref={dropdownRef}
            className="block dropdown dropdown-end [&[open]>summary>svg]:rotate-180"
          >
            <summary className="flex gap-1 bg-transparent border-0 shadow-none outline-none hover:outline-none hover:border-0 btn hover:bg-transparent">
              <div
                tabIndex={0}
                role="button"
                className="p-0 bg-transparent border-0 outline-none btn btn-circle avatar hover:bg-transparent hover:outline-none hover:border-0"
              >
                <div className="w-12 text-white text-opacity-75 rounded-full bg-primary">
                  <PersonIcon className="object-cover w-full h-full p-2" />
                </div>
              </div>
              <ExpandMoreRoundedIcon
                fontSize="large"
                className="text-white transition-transform opacity-50"
              />
            </summary>

            <div className="dropdown-content mt-3 z-[1] p-8 bg-base-100 rounded-2xl border border-gray-line ">
              <h2 className="mb-4 text-3xl font-medium text-center text-primary">
                Bentornato!
              </h2>
              <p className="mb-8 text-center text-neutral">
                Accedi o registrati gratuitamente per iniziare a generare
                contenuti.
              </p>
              <Login
                onSuccess={handleLoginSuccess}
                switchToSignUp={handleSwitchToSignUp}
              />
            </div>
          </details>
        )}
      </div>
    </div>
  );
};

export default Navbar;
